import * as React from 'react';

import {
  enumLoadStatus,
  enumUnit,
  LoadStatus,
} from '@rematter/frontend-lib-api-client';
import {
  DataGridColumn,
  LocalizedDate,
  maybeEmptyInfo,
  MeasuredValue,
  Money,
} from '@rematter/ui-web';
import { useAtom, useAtomValue } from 'jotai';

import {
  $columnDateRange,
  $columnSelectedCustomerIds,
  $columnSelectedFacilityIds,
  $columnSelectedStatuses,
  $selectedCreatedByUserIds,
} from '../../../_state/loadsList.state';
import { LoadRecord } from '../../../types/load';
import { CreatedByFilter } from '../../_shared/filters/CreatedByFilter';
import { LoadStatusBadge } from '../../_shared/StatusBadges';
import { CustomerColumnFilter } from '../filters/CustomerColumnFilter';
import { DateRangeColumnFilter } from '../filters/DateRangeColumnFilter';
import { FacilityColumnFilter } from '../filters/FacilityColumnFilter';
import { StatusColumnFilter } from '../filters/StatusColumnFilter';
import { LoadActionMenu } from '../LoadActionMenu';

import { FacilityRecord } from '~/modules/trade/_graphql/queryFacilities';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';

const ENABLED_COLUMNS_BY_STATUS: Record<LoadStatus, string[]> = {
  Unassigned: [
    'expectedShipDate',
    'loadNumber',
    'salesOrderNumber',
    'facilityName',
    'lineItemCount',
    'netQuantity',
    'createdOn',
    'createdBy',
    'actions',
  ],
  Pending: [
    'expectedShipDate',
    'loadNumber',
    'salesOrderNumber',
    'bookingNumber',
    'containerNumber',
    'releaseNumber',
    'shippingCarrierName',
    'customer',
    'facilityName',
    'lineItemCount',
    'netQuantity',
    'status',
    'createdOn',
    'createdBy',
    'actions',
  ],
  ContainerAtPort: [
    'expectedShipDate',
    'loadNumber',
    'salesOrderNumber',
    'bookingNumber',
    'containerNumber',
    'releaseNumber',
    'customer',
    'facilityName',
    'lineItemCount',
    'netQuantity',
    'status',
    'createdOn',
    'actions',
  ],
  ContainerInTransit: [
    'expectedShipDate',
    'loadNumber',
    'salesOrderNumber',
    'bookingNumber',
    'containerNumber',
    'releaseNumber',
    'customer',
    'facilityName',
    'lineItemCount',
    'netQuantity',
    'status',
    'createdOn',
    'actions',
  ],
  ContainerAtFacility: [
    'expectedShipDate',
    'loadNumber',
    'salesOrderNumber',
    'bookingNumber',
    'containerNumber',
    'releaseNumber',
    'customer',
    'facilityName',
    'lineItemCount',
    'netQuantity',
    'status',
    'createdOn',
    'actions',
  ],
  ContainerLoaded: [
    'expectedShipDate',
    'loadNumber',
    'salesOrderNumber',
    'bookingNumber',
    'containerNumber',
    'releaseNumber',
    'customer',
    'facilityName',
    'lineItemCount',
    'netQuantity',
    'status',
    'createdOn',
    'actions',
  ],
  ContainerReturning: [
    'expectedShipDate',
    'loadNumber',
    'salesOrderNumber',
    'bookingNumber',
    'containerNumber',
    'releaseNumber',
    'customer',
    'facilityName',
    'lineItemCount',
    'netQuantity',
    'status',
    'createdOn',
    'actions',
  ],
  ContainerReadyToShip: [
    'expectedShipDate',
    'loadNumber',
    'salesOrderNumber',
    'bookingNumber',
    'containerNumber',
    'releaseNumber',
    'customer',
    'facilityName',
    'lineItemCount',
    'netQuantity',
    'status',
    'createdOn',
    'actions',
  ],
  Shipped: [
    'shippedDate',
    'loadNumber',
    'salesOrderNumber',
    'bookingNumber',
    'containerNumber',
    'releaseNumber',
    'shippingCarrierName',
    'customer',
    'facilityName',
    'lineItemCount',
    'status',
    'shippedNetWeight',
    'shippedTotal',
    'createdOn',
    'createdBy',
    'actions',
  ],
  Received: [
    'shippedDate',
    'loadNumber',
    'salesOrderNumber',
    'bookingNumber',
    'containerNumber',
    'releaseNumber',
    'shippingCarrierName',
    'customer',
    'facilityName',
    'lineItemCount',
    'status',
    'shippedNetWeight',
    'shippedTotal',
    'receivingTicketNumber',
    'createdOn',
    'createdBy',
    'actions',
  ],
  Reconciled: [
    'shippedDate',
    'loadNumber',
    'salesOrderNumber',
    'bookingNumber',
    'containerNumber',
    'releaseNumber',
    'shippingCarrierName',
    'customer',
    'facilityName',
    'lineItemCount',
    'status',
    'shippedNetWeight',
    'shippedTotal',
    'receivingTicketNumber',
    'createdOn',
    'createdBy',
    'actions',
  ],
  Voided: [],
};

export const useLoadsListColumns = ({
  status,
  facilities,
  onRefetch,
  onVoid,
  onDoubleClick,
  onReconcile,
  onMarkAsShipped,
  onMarkAsReceived,
}: {
  status: LoadStatus;
  facilities: FacilityRecord[];
  onClick?: (row: LoadRecord) => void;
  onDoubleClick?: (row: LoadRecord) => void;
  onRefetch?: () => void;
  onVoid?: (loadId: string) => void;
  onReconcile?: (load: LoadRecord) => void;
  onMarkAsShipped: (loadId: string) => void;
  onMarkAsReceived: (loadId: string) => void;
}) => {
  const t = useTranslations();

  // Column filter states
  const columnDateRange = useAtomValue($columnDateRange);
  const columnSelectedCustomerIds = useAtomValue($columnSelectedCustomerIds);
  const columnSelectedFacilityIds = useAtomValue($columnSelectedFacilityIds);
  const columnSelectedStatuses = useAtomValue($columnSelectedStatuses);
  const [selectedCreatedByUserIds, setSelectedCreatedByUserIds] = useAtom(
    $selectedCreatedByUserIds,
  );

  const handleCellDoubleClick = React.useCallback(
    ({ row }: { row: LoadRecord }) => {
      onDoubleClick?.(row);
    },
    [onDoubleClick],
  );

  const columns = React.useMemo(() => {
    const columnDefs: DataGridColumn<LoadRecord>[] = [
      {
        key: 'expectedShipDate',
        header: t.loadsView.table.columns.expectedShipDate,
        sortable: true,
        cell: ({ row }) =>
          maybeEmptyInfo(row.expectedShipDate, (v) => (
            <LocalizedDate date={v} />
          )),
        columnFilter: ({ close }) => <DateRangeColumnFilter onClose={close} />,
        isFilterActive: !!columnDateRange,
      },
      {
        key: 'shippedDate',
        header: t.loadsView.table.columns.shippedDate,
        sortable: true,
        cell: ({ row }) =>
          maybeEmptyInfo(row.shippedDate, (v) => <LocalizedDate date={v} />),
      },
      {
        key: 'salesOrderNumber',
        header: t.loadsView.table.columns.salesOrderNumber,
        cell: ({ row }) => maybeEmptyInfo(row.salesOrderNumber),
      },
      {
        key: 'loadNumber',
        header: t.loadsView.table.columns.loadNumber,
        cell: ({ row }) => maybeEmptyInfo(row.loadNumber),
      },
      {
        key: 'bookingNumber',
        header: t.loadsView.table.columns.bookingNumber,
        cell: ({ row }) => maybeEmptyInfo(row.bookingNumber),
      },
      {
        key: 'containerNumber',
        header: t.loadsView.table.columns.containerNumber,
        cell: ({ row }) => maybeEmptyInfo(row.containerNumber),
      },
      {
        key: 'releaseNumber',
        header: t.loadsView.table.columns.releaseNumber,
        cell: ({ row }) => maybeEmptyInfo(row.releaseNumber),
      },
      {
        key: 'shippingCarrierName',
        header: t.loadsView.table.columns.shippingCarrier,
        cell: ({ row }) => maybeEmptyInfo(row.shippingCarrierName),
      },
      {
        key: 'customer',
        header: t.loadsView.table.columns.customer,
        cell: ({ row }) => maybeEmptyInfo(row.customer),
        columnFilter: ({ close }) => <CustomerColumnFilter onClose={close} />,
        isFilterActive: columnSelectedCustomerIds.size > 0,
      },
      {
        key: 'facilityName',
        header: t.loadsView.table.columns.facility,
        cell: ({ row }) => {
          const facility = row.facilityId
            ? facilities.find((f) => f.id === row.facilityId)
            : null;

          return maybeEmptyInfo(facility?.facilityName);
        },
        columnFilter: ({ close }) => <FacilityColumnFilter onClose={close} />,
        isFilterActive: columnSelectedFacilityIds.size > 0,
      },
      {
        key: 'lineItemCount',
        header: t.loadsView.table.columns.lineItemCount,
        cell: ({ row }) => maybeEmptyInfo(row.lineItemCount),
      },
      {
        key: 'netQuantity',
        header: t.loadsView.table.columns.netQuantity,
        cell: ({ row }) =>
          status === enumLoadStatus.Pending
            ? row.netRequestedQuantity
            : row.netQuantity,
      },
      {
        key: 'status',
        header: t.loadsView.table.columns.status,
        sortable: true,
        cell: ({ row }) =>
          maybeEmptyInfo(row.status, (v) => (
            <LoadStatusBadge status={v} isReviewed={row.reviewed} />
          )),
        columnFilter: ({ close }) => <StatusColumnFilter onClose={close} />,
        isFilterActive: columnSelectedStatuses.size > 0,
      },
      {
        key: 'createdOn',
        header: t.loadsView.table.columns.createdOn,
        sortable: true,
        cell: ({ row }) =>
          maybeEmptyInfo(row.timestamps?.createdAt, (v) => (
            <LocalizedDate date={v} />
          )),
      },
      {
        key: 'createdBy',
        header: t.loadsView.table.columns.createdBy,
        sortable: true,
        columnFilter: ({ close }) => (
          <CreatedByFilter
            selectedUserIds={selectedCreatedByUserIds}
            onSelectionChange={(userIds) => {
              setSelectedCreatedByUserIds(
                new Set(Array.from(userIds, (key) => String(key))),
              );
            }}
            onReset={() => {
              setSelectedCreatedByUserIds(new Set());
            }}
            onClose={close}
          />
        ),
        isFilterActive: selectedCreatedByUserIds.size > 0,
        cell: ({ row }) => {
          const createdByUser = row.createdByUser;
          const displayName = createdByUser
            ? `${createdByUser.firstName} ${createdByUser.lastName}`
            : row.createdBy;

          return maybeEmptyInfo(displayName);
        },
      },
      {
        key: 'shippedNetWeight',
        header: t.loadsView.table.columns.netQuantity,
        cell: ({ row }) => (
          <MeasuredValue value={row.netQuantity ?? 0} unit={enumUnit.Pounds} />
        ),
      },
      {
        key: 'shippedTotal',
        header: t.loadsView.table.columns.shippedTotal,
        cell: ({ row }) => (
          <div className="text-right">
            <Money amount={row.shippedTotal} />
          </div>
        ),
      },
      {
        key: 'receivingTicketNumber',
        header: t.loadsView.table.columns.receivingTicketNumber,
        cell: ({ row }) => maybeEmptyInfo(row.receivingTicketNumber),
      },
      {
        key: 'actions',
        header: '',
        width: 60,
        pinned: 'right',
        cell: ({ row }) => {
          const load = row as LoadRecord;
          return (
            <LoadActionMenu
              load={load}
              onVoid={onVoid}
              onReconcile={onReconcile}
              onMarkAsShipped={onMarkAsShipped}
              onMarkAsReceived={onMarkAsReceived}
              onRefetch={onRefetch}
            />
          );
        },
      },
    ];

    return columnDefs.map((it) => ({
      ...it,
      onDoubleClick: it.key === 'actions' ? undefined : handleCellDoubleClick,
    }));
  }, [
    t,
    facilities,
    status,
    onVoid,
    onReconcile,
    onMarkAsShipped,
    onMarkAsReceived,
    onRefetch,
    handleCellDoubleClick,
    columnDateRange,
    columnSelectedCustomerIds,
    columnSelectedFacilityIds,
    columnSelectedStatuses,
    selectedCreatedByUserIds,
    setSelectedCreatedByUserIds,
  ]);

  const columnVisibilityState = React.useMemo(() => {
    const enabledColumns =
      ENABLED_COLUMNS_BY_STATUS[status] ??
      ENABLED_COLUMNS_BY_STATUS[enumLoadStatus.Reconciled];

    return Object.fromEntries(
      columns.map((column) => [
        column.key,
        enabledColumns.includes(column.key),
      ]),
    );
  }, [status, columns]);

  return { columns, columnVisibilityState };
};
